import React, { useState, useCallback } from 'react';
import { AñoEscolar, Grado, Seccion } from 'types/usuario.types';
import { ReporteCredencialesEstudiantes, EstudianteCredencial } from 'types/reporte.types';
import { catalogoService } from 'services/usuarioService';
import { reporteService } from 'services/reporteService';
import { exportarExcel, exportarPDF } from 'utils/reporteExport';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// CredencialesEstudiantesPage
// Reporte: credenciales (usuario + contraseña) de estudiantes
// por sección — GET /reportes/usuarios/estudiantes
// ============================================================

const NIVELES = ['INICIAL', 'PRIMARIA', 'SECUNDARIA'];

const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_SEARCH   = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z';
const D_EXCEL    = 'M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z';
const D_PDF      = 'M4 4a2 2 0 0 1 2-2h4.586A2 2 0 0 1 12 2.586L15.414 6A2 2 0 0 1 16 7.414V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4Z';
const D_EYE     = 'M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41Z';
const D_EYE_OFF = 'M3.28 2.22a.75.75 0 0 0-1.06 1.06l14.5 14.5a.75.75 0 1 0 1.06-1.06l-1.745-1.745a10.029 10.029 0 0 0 3.3-4.38 1.651 1.651 0 0 0 0-1.185A10.004 10.004 0 0 0 9.999 3a9.956 9.956 0 0 0-4.744 1.194L3.28 2.22ZM7.752 6.69l1.092 1.092a2.5 2.5 0 0 1 3.374 3.373l1.091 1.092a4 4 0 0 0-5.557-5.557Z';

const Spin = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const CredencialesEstudiantesPage: React.FC = () => {
  const [añosEscolares, setAñosEscolares] = useState<AñoEscolar[]>([]);
  const [grados, setGrados]               = useState<Grado[]>([]);
  const [secciones, setSecciones]         = useState<Seccion[]>([]);

  const [anioId, setAnioId]     = useState<number | ''>('');
  const [nivel, setNivel]       = useState('');
  const [gradoId, setGradoId]   = useState<number | ''>('');
  const [seccionId, setSeccionId] = useState<number | ''>('');

  const [data, setData]         = useState<ReporteCredencialesEstudiantes | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [showPass, setShowPass] = useState(false);

  // Catálogos al montar
  React.useEffect(() => {
    catalogoService.getAñosEscolares().then(a => {
      setAñosEscolares(a);
      if (a.length) setAnioId(a[0].idAnioEscolar);
    });
    catalogoService.getGrados().then(setGrados);
    catalogoService.getSecciones().then(setSecciones);
  }, []);

  const gradosPorNivel  = nivel ? grados.filter(g => g.nivel === nivel) : grados;
  const seccionesPorGrado = secciones; // las secciones son independientes del grado (A, B, C…)

  const handleBuscar = useCallback(async () => {
    if (!anioId) { setError('Selecciona un año escolar'); return; }
    if (!gradoId) { setError('Selecciona un grado'); return; }
    if (!seccionId) { setError('Selecciona una sección'); return; }
    setLoading(true); setError(null); setData(null);
    try {
      const res = await reporteService.credencialesEstudiantes({
        anioEscolarId: Number(anioId),
        ...(nivel    && { nivel }),
        ...(gradoId  && { gradoId:   Number(gradoId) }),
        ...(seccionId && { seccionId: Number(seccionId) }),
      });
      setData(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar reporte');
    } finally { setLoading(false); }
  }, [anioId, nivel, gradoId, seccionId]);

  // ── Exportar Excel ──────────────────────────────────────────
  const handleExcel = () => {
    if (!data) return;
    const titulo = `${data.gradoNivel} ${data.gradoNombre}° "${data.seccionNombre}" – ${data.anio}`;
    exportarExcel(`credenciales_${data.anio}_${data.gradoNombre}_${data.seccionNombre}`, [{
      nombre: 'Credenciales',
      filas: [
        [`Reporte de Credenciales — ${titulo}`],
        [`Total estudiantes: ${data.totalEstudiantes}`],
        [],
        ['N°', 'Nombres', 'Apellidos', 'DNI', 'Cód. SIAGIE', 'Usuario',
         'Contraseña por defecto', 'Contraseña modificada', 'Últ. cambio contraseña',
         'Email', 'Teléfono', 'Estado'],
        ...data.estudiantes.map((e, i) => [
          i + 1, e.nombres, e.apellidos, e.dni,
          e.codigoSiagie ?? '', e.username,
          e.passwordDefault ?? '(personalizada)',
          e.passwordModificado ? 'Sí' : 'No',
          e.fechaModificacionPassword ?? '—',
          e.email ?? '', e.telefono ?? '', e.activo ? 'Activo' : 'Inactivo',
        ]),
      ],
    }]);
  };

  // ── Exportar PDF ────────────────────────────────────────────
  const handlePDF = () => {
    if (!data) return;
    const titulo = `Credenciales — ${data.gradoNivel} ${data.gradoNombre}° "${data.seccionNombre}" ${data.anio}`;
    const filas = data.estudiantes.map((e, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${e.nombres} ${e.apellidos}</td>
        <td style="font-family:monospace">${e.dni}</td>
        <td>${e.codigoSiagie ?? '—'}</td>
        <td style="font-family:monospace">${e.username}</td>
        <td style="font-family:monospace">${e.passwordDefault ?? '—'}</td>
        <td><span class="badge" style="background:${e.passwordModificado ? '#fef3c7' : '#f0fdf4'};color:${e.passwordModificado ? '#92400e' : '#166534'}">${e.passwordModificado ? 'Sí' : 'No'}</span></td>
        <td style="font-size:9px">${e.fechaModificacionPassword ?? '—'}</td>
        <td style="font-size:9px">${e.email ?? '—'}</td>
        <td><span class="badge" style="background:${e.activo ? '#dcfce7' : '#f1f5f9'};color:${e.activo ? '#166534' : '#64748b'}">${e.activo ? 'Activo' : 'Inactivo'}</span></td>
      </tr>`).join('');
    exportarPDF(titulo, `
      <div class="kpi-row">
        <div class="kpi"><div class="kpi-lbl">Año</div><div class="kpi-val">${data.anio}</div></div>
        <div class="kpi"><div class="kpi-lbl">Nivel</div><div class="kpi-val">${data.gradoNivel}</div></div>
        <div class="kpi"><div class="kpi-lbl">Grado</div><div class="kpi-val">${data.gradoNombre}°</div></div>
        <div class="kpi"><div class="kpi-lbl">Sección</div><div class="kpi-val">${data.seccionNombre}</div></div>
        <div class="kpi"><div class="kpi-lbl">Total</div><div class="kpi-val">${data.totalEstudiantes}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>#</th><th>Estudiante</th><th>DNI</th><th>SIAGIE</th>
          <th>Usuario</th><th>Contraseña por defecto</th><th>Modificada</th>
          <th>Últ. cambio</th><th>Email</th><th>Estado</th>
        </tr></thead>
        <tbody>${filas}</tbody>
      </table>`);
  };

  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div>
        <h1 className="text-xl font-bold text-slate-800">Credenciales de Estudiantes</h1>
        <p className="text-slate-400 text-sm mt-0.5">
          Consulta usuarios y contraseñas por sección
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {/* Año */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">
              Año escolar <span className="text-red-400">*</span>
            </label>
            <select value={anioId} onChange={e => { setAnioId(Number(e.target.value) || ''); setNivel(''); setGradoId(''); setSeccionId(''); }}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm
                         text-slate-700 outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Seleccionar…</option>
              {añosEscolares.map(a => (
                <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
              ))}
            </select>
          </div>

          {/* Nivel — requiere año */}
          <div>
            <label className={`block text-xs font-bold uppercase tracking-wide mb-1
              ${anioId ? 'text-slate-500' : 'text-slate-300'}`}>
              Nivel <span className="text-red-400">*</span>
            </label>
            <select value={nivel}
              disabled={!anioId}
              onChange={e => { setNivel(e.target.value); setGradoId(''); setSeccionId(''); }}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm
                         text-slate-700 outline-none focus:ring-2 focus:ring-blue-500
                         disabled:opacity-40 disabled:cursor-not-allowed">
              <option value="">Seleccionar…</option>
              {NIVELES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          {/* Grado — requiere año + nivel */}
          <div>
            <label className={`block text-xs font-bold uppercase tracking-wide mb-1
              ${anioId && nivel ? 'text-slate-500' : 'text-slate-300'}`}>
              Grado <span className="text-red-400">*</span>
            </label>
            <select value={gradoId}
              disabled={!anioId || !nivel}
              onChange={e => { setGradoId(Number(e.target.value) || ''); setSeccionId(''); }}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm
                         text-slate-700 outline-none focus:ring-2 focus:ring-blue-500
                         disabled:opacity-40 disabled:cursor-not-allowed">
              <option value="">Seleccionar…</option>
              {gradosPorNivel.map(g => (
                <option key={g.idGrado} value={g.idGrado}>{g.nombre}</option>
              ))}
            </select>
          </div>

          {/* Sección — requiere año + nivel + grado */}
          <div>
            <label className={`block text-xs font-bold uppercase tracking-wide mb-1
              ${anioId && nivel && gradoId ? 'text-slate-500' : 'text-slate-300'}`}>
              Sección <span className="text-red-400">*</span>
            </label>
            <select value={seccionId}
              disabled={!anioId || !nivel || !gradoId}
              onChange={e => setSeccionId(Number(e.target.value) || '')}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm
                         text-slate-700 outline-none focus:ring-2 focus:ring-blue-500
                         disabled:opacity-40 disabled:cursor-not-allowed">
              <option value="">Seleccionar…</option>
              {seccionesPorGrado.map(s => (
                <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button onClick={handleBuscar} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                       text-white text-sm font-semibold transition-colors shadow-sm disabled:opacity-60">
            {loading ? <Spin /> : <Ico d={D_SEARCH} />}
            {loading ? 'Consultando…' : 'Consultar'}
          </button>

          {data && (
            <>
              <button onClick={handleExcel}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-emerald-200
                           text-emerald-700 hover:bg-emerald-50 text-sm font-semibold transition-colors">
                <Ico d={D_EXCEL} />
                Excel
              </button>
              <button onClick={handlePDF}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-200
                           text-red-600 hover:bg-red-50 text-sm font-semibold transition-colors">
                <Ico d={D_PDF} />
                PDF
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Tabla de resultados */}
      {data && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">

          {/* Info de sección */}
          <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center gap-4">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide font-bold">Sección</p>
              <p className="font-bold text-slate-800 text-sm">
                {data.gradoNivel} — {data.gradoNombre}° "{data.seccionNombre}" · {data.anio}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <span className="text-xs text-slate-500 font-semibold">
                {data.totalEstudiantes} estudiante{data.totalEstudiantes !== 1 ? 's' : ''}
              </span>
              {/* Toggle mostrar/ocultar contraseñas */}
              <button
                onClick={() => setShowPass(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                            border transition-colors
                            ${showPass
                              ? 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                              : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                <Ico d={showPass ? D_EYE_OFF : D_EYE} cls="w-3.5 h-3.5" />
                {showPass ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['#', 'Estudiante', 'DNI', 'Cód. SIAGIE', 'Usuario', 'Contraseña por defecto', 'Modificada', 'Email', 'Estado'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-400
                                           uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.estudiantes.map((e: EstudianteCredencial, i: number) => (
                  <tr key={e.idUsuario} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-xs text-slate-400 tabular-nums">{i + 1}</td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center
                                        bg-blue-100 text-blue-700 font-bold text-xs">
                          {e.nombres.charAt(0)}{e.apellidos.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800 leading-tight">
                            {e.nombres} {e.apellidos}
                          </p>
                          <p className="text-xs text-slate-400">Matrícula #{e.idMatricula}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 font-mono text-slate-600 whitespace-nowrap">{e.dni}</td>

                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {e.codigoSiagie ?? <span className="text-slate-300">—</span>}
                    </td>

                    <td className="px-4 py-3 font-mono text-slate-700 font-semibold whitespace-nowrap">
                      {e.username}
                    </td>

                    {/* Contraseña por defecto */}
                    <td className="px-4 py-3">
                      {e.passwordDefault ? (
                        <span className={`font-mono text-xs ${showPass ? 'text-slate-700' : 'text-slate-300 select-none'}`}>
                          {showPass ? e.passwordDefault : '••••••••••••'}
                        </span>
                      ) : (
                        <span className="text-xs text-amber-600 font-semibold">personalizada</span>
                      )}
                    </td>

                    {/* ¿Modificada? */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {e.passwordModificado ? (
                        <div>
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5
                                           rounded-full bg-amber-100 text-amber-700">
                            Sí
                          </span>
                          {e.fechaModificacionPassword && (
                            <p className="text-xs text-slate-400 mt-0.5">
                              {e.fechaModificacionPassword.split(' ')[0]}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5
                                         rounded-full bg-green-100 text-green-700">
                          No
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {e.email ?? <span className="text-slate-300">—</span>}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full
                        ${e.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${e.activo ? 'bg-green-500' : 'bg-slate-400'}`} />
                        {e.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CredencialesEstudiantesPage;
